import { Component, OnInit, OnDestroy, Input, ViewChild, EventEmitter, Output } from '@angular/core';
import { Router } from '@angular/router';
// ionic
import { ModalController } from '@ionic/angular';
import { AlertController, PopoverController } from '@ionic/angular';
import { Events } from '../../helpers/events';

// other libraries
import { TranslateService } from "@ngx-translate/core";

// rxjs
import { Subject, Subscription } from "rxjs";
import { takeUntil, debounceTime } from "rxjs/operators";

// services
import { EventService } from "../../services/event.service";
import { PlatformService } from "../../services/platform.service";
import { ParticipantService } from "../../services/participant.service";
import { OverlayService } from "../../services/overlay.service";
import { UserService } from "../../services/user.service";
import { ConversationService } from "../../services/conversation.service";
import { GeocodeService } from "../../services/geocode.service";

// components
import { CountyFilterComponent } from "../county-filter/county-filter.component";
import { AutoCompleteComponent } from "../auto-complete/auto-complete.component";

// models
import { Appointment } from "../../models/appointment";
import { Event } from "../../models/event";
import { Conversation } from "../../models/conversation";
import { Container } from "../../models/container";
import { Group } from "../../models/group";
import { Keyword } from "../../models/keyword";
import { Participant } from "../../models/participant";
import { User } from "../../models/user";

@Component({
    selector: 'community-filter',
    templateUrl: './community-filter.component.html',
    styleUrls: ['./community-filter.component.scss'],
})
export class CommunityFilterComponent implements OnInit, OnDestroy {

    @ViewChild('autocomplete') searchbar: AutoCompleteComponent;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * event
     *
     * @type {Event}
     */
    public event: Event;

    /**
     * user
     *
     * @type {User}
     */
    public user: User = new User;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * search string for attendees list
     *
     * @type {String}
     */
    @Input() attendeesSearch: string = '';

    @Input() selectedKeywordIds: any[] = [];

    @Input() selectedCounties: any[] = [];

    @Input() modal: boolean = true;

    @Input() enableDistance: boolean = true;

    @Output() applyFilter: EventEmitter<{}> = new EventEmitter();

    /**
     * show loading
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     * show submit
     *
     * @type {boolean}
     */
    public submit: boolean = false;

    @Input() selectedAddress: Object = null;
    @Input() selectedDistance: number = 20;

    /**
     * which select type should be used.. alert/popover/action-sheet
     *
     * @type {string}
     */
    public selectType: string = 'popover';

    /**
     * participant has some keyword setting
     *
     * @type {boolean}
     */
    public visibleAllKeywords: boolean = false;

    public keywordContainerLimits: number = 1;

    public fulltextMinSearchLenght: number = 3;

    constructor(
        public plt: PlatformService,
        public appEvents: Events,
        public conversationService: ConversationService,
        public eventService: EventService,
        public participantService: ParticipantService,
        public geocodeService: GeocodeService,
        public popoverController: PopoverController,
        public overlayService: OverlayService,
        public translate: TranslateService,
        public userService: UserService,
        public modalController: ModalController,
        public alertCtrl: AlertController,
        public router: Router
    ) {

        // user language
        this.userLang = this.translate.currentLang;
        if (this.plt.sizeSm) {
            this.selectType = 'action-sheet';
        }

        if (this.plt.sizeXl) {
            this.keywordContainerLimits = 3;
        }
    }

    ngOnInit() {
        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {
                this.user = user;

                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }

                this.subscriptionEvent = this.eventService.getCurrentEvent().pipe(
                    takeUntil(this.ngUnsubscribe),
                    debounceTime(0)
                ).subscribe((event) => {
                    this.event = event;
                    this.loading = false;

                    // order keywords
                    this.event.containers.forEach((container) => {
                        container.orderKeywordsByName(this.userLang, this.event.default_locale);
                    });
                });
            } else {
                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }
            }
        });
    }

    /**
     * toggle keyword selection
     *
     * @param column
     * @param keyword
     * @returns {boolean}
     */
    public toggleKeyword(column: number, keyword: Keyword) {
        if (this.selectedKeywordIds.filter((item) => { return item.id == keyword.id && item.column == column }).length) {
            this.selectedKeywordIds = this.selectedKeywordIds.filter((item) => {
                return item.id != keyword.id || item.column != column;
            });
        } else {
            this.selectedKeywordIds.push({
                id: keyword.id,
                column: column
            });
        }
    }

    /**
     * Check, if the keyword is selected
     *
     * @param column
     * @param keyword
     * @returns {boolean}
     */
    public isSelected(column: number, keyword: Keyword) {
        if (this.selectedKeywordIds && this.selectedKeywordIds.filter((item) => { return item.id == keyword.id && item.column == column }).length) {
            return true;
        }

        return false;
    }

    /**
     * close modal window
     *
     */
    public backClicked() {
        this.modalController.dismiss({
            action: 'close'
        });
    }

    public seeResults() {
        if (!this.submit) {
            this.submit = true;
            if (this.modal) {
                this.submit = false;
                this.modalController.dismiss({
                    action: 'close',
                    attendeesSearch: this.attendeesSearch,
                    selectedKeywordIds: this.selectedKeywordIds,
                    selectedCounties: this.selectedCounties,
                    selectedAddress: this.selectedAddress,
                    selectedDistance: this.selectedDistance
                });
            } else {
                setTimeout(() => {
                    this.submit = false;
                    if (this.attendeesSearch.length >= this.fulltextMinSearchLenght || this.selectedKeywordIds.length > 0) {
                        this.applyFilter.emit({
                            action: 'close',
                            attendeesSearch: this.attendeesSearch,
                            selectedKeywordIds: this.selectedKeywordIds,
                            selectedCounties: this.selectedCounties,
                            selectedAddress: this.selectedAddress,
                            selectedDistance: this.selectedDistance
                        });
                    }
                }, 300);
            }
        }
    }

    public resetResults() {
        this.attendeesSearch = '';
        this.selectedKeywordIds = [];
        this.selectedAddress = '';
        this.selectedCounties = [];
        this.selectedDistance = 20;

        this.applyFilter.emit({
            action: 'close',
            attendeesSearch: this.attendeesSearch,
            selectedKeywordIds: this.selectedKeywordIds,
            selectedCounties: this.selectedCounties,
            selectedAddress: this.selectedAddress,
            selectedDistance: this.selectedDistance
        });
    }

    /**
     * show popover with attendees filter
     *
     * @param event
     *
     * @return void
     */
    async showCountyFilter(ev: any) {
        const popover = await this.popoverController.create({
            component: CountyFilterComponent,
            event: ev,
            cssClass: 'county:filter',
            componentProps: {
                'counties': this.event.counties,
                'selected': this.selectedCounties
            }
        });

        await popover.present();
    }

    /**
     * check app resize (keyboard opening) for input focus
     */
    public autocompleteFocus() {
        this.plt.checkResize();
        // we will hide on small screens all to make autocomplete input bigger
        // this.autocompleteOpen = true;
    }

    /**
     * check app resize (keyboard closing) for input blur
     */
    public autocompleteBlur(clear: boolean = false) {
        this.plt.checkResize(false);
        // this.autocompleteOpen = false;
        // close autocomplete list when triggering manually cancel for autocomplete
        if (clear) {
            // NOTE[jg] do this only for manual, not on ionBlur for autocomplete component
            // that was causing big performance problems
            this.searchbar.clearValue(true);
        }
    }

    /**
     * set selected address to filter
     *
     * @param {any} address
     */
    public setAddress(address) {
        this.selectedAddress = address;
        // check resize of app and other items when bluring input
        this.autocompleteBlur();

        // reset value for autocomplet search
        // this.searchbar.clearValue();
        // this.scrollBottom();
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.appEvents.unsubscribe('county:filter');

        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
